import React, { useMemo, useRef, useState } from "react";
import {
  Box, Button, Grid, TextField, Typography, Paper, IconButton, CircularProgress,
  Divider, Chip, InputAdornment,
} from "@mui/material";
import { useFormik } from "formik";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import NotesIcon from "@mui/icons-material/Notes";
import SearchIcon from "@mui/icons-material/Search";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useCreateVendorMutation } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router";
import { uploadImage } from "../../../Components/UploadFile/upload";
import { parseNominatimResult } from "../../../utils/parseAddress";

// ─── Phone helpers ───────────────────────────────────────────────────────────
const formatPhone = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const stripPhone = (formatted: string): string => formatted.replace(/\D/g, "");

const validatePhone = (value: string): string | undefined => {
  if (!value) return undefined;
  const digits = stripPhone(value);
  if (digits.length > 0 && digits.length !== 10)
    return "Phone must be 10 digits — e.g. (416) 555-1234";
  return undefined;
};

// ─── Email helper ────────────────────────────────────────────────────────────
const validateEmail = (value: string): string | undefined => {
  if (!value) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? undefined : "Invalid email address";
};

// ─── Nominatim address type ──────────────────────────────────────────────────
interface NominatimResult {
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    postcode?: string;
  };
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 },
  },
};

const labelSx = { fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 };

const Index = () => {
  const navigate = useNavigate();
  const [newTax, { isLoading }] = useCreateVendorMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);

  // Address autocomplete state
  const [addressOptions, setAddressOptions] = useState<NominatimResult[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressInput, setAddressInput] = useState("");
  const [addressOpen, setAddressOpen] = useState(false);
  const addressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchAddress = async (query: string) => {
    if (query.trim().length < 4) { setAddressOptions([]); setAddressOpen(false); return; }
    setAddressLoading(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&countrycodes=ca&limit=6`,
        { headers: { "Accept-Language": "en", "User-Agent": "POS-App/1.0" } }
      );
      const data: NominatimResult[] = await res.json();
      setAddressOptions(data);
      setAddressOpen(data.length > 0);
    } catch { setAddressOptions([]); }
    finally { setAddressLoading(false); }
  };

  const handleAddressInputChange = (value: string) => {
    setAddressInput(value);
    if (addressTimer.current) clearTimeout(addressTimer.current);
    addressTimer.current = setTimeout(() => searchAddress(value), 450);
  };

  const handleAddressSelect = (result: NominatimResult) => {
    const parsed = parseNominatimResult(result);
    formik.setFieldValue("customerStreetNumber", parsed.streetNumber);
    formik.setFieldValue("customerStreetName", parsed.streetName);
    formik.setFieldValue("customerCity", parsed.city);
    formik.setFieldValue("customerProvince", parsed.province);
    formik.setFieldValue("customerPostalCode", parsed.postalCode);
    setAddressInput(result.display_name);
    setAddressOptions([]);
    setAddressOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      customerCompanyName: "", customerCompanyPhoneNumebr: "", customerCompanyEmail: "",
      customerFirstName: "", customerLastName: "", customerMobileNum: "", customerEmail: "",
      customerProvince: "", customerCity: "", customerPostalCode: "",
      customerStreetNumber: "", customerStreetName: "", customerDescription: "", customerImg: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.customerCompanyName.trim()) errors.customerCompanyName = "Supplier name is required";
      const compPhoneErr = validatePhone(values.customerCompanyPhoneNumebr);
      if (compPhoneErr) errors.customerCompanyPhoneNumebr = compPhoneErr;
      const compEmailErr = validateEmail(values.customerCompanyEmail);
      if (compEmailErr) errors.customerCompanyEmail = compEmailErr;
      const mobileErr = validatePhone(values.customerMobileNum);
      if (mobileErr) errors.customerMobileNum = mobileErr;
      const emailErr = validateEmail(values.customerEmail);
      if (emailErr) errors.customerEmail = emailErr;
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let customerImg: string | undefined;
        if (selectedImage) customerImg = (await uploadImage(selectedImage)) || "";
        const payload = {
          ...values,
          customerCompanyPhoneNumebr: stripPhone(values.customerCompanyPhoneNumebr),
          customerMobileNum: stripPhone(values.customerMobileNum),
          customerImg,
        };
        const res = await newTax(payload).unwrap();
        if (!res.status) showErrorMessage(res.message || "Failed to create supplier");
        else { showMessage("Supplier created successfully"); navigate("/vendor/list"); resetForm(); setSelectedImage(undefined); }
      } catch { showErrorMessage("Something went wrong"); }
    },
  });

  const handlePhoneChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue(field, formatPhone(e.target.value));
  };

  const formValid = useMemo(() => {
    if (!formik.values.customerCompanyName.trim()) return false;
    const cp = validatePhone(formik.values.customerCompanyPhoneNumebr);
    const mp = validatePhone(formik.values.customerMobileNum);
    const ce = validateEmail(formik.values.customerCompanyEmail);
    const pe = validateEmail(formik.values.customerEmail);
    if (cp || mp || ce || pe) return false;
    return true;
  }, [formik.values.customerCompanyName, formik.values.customerCompanyPhoneNumebr, formik.values.customerMobileNum, formik.values.customerCompanyEmail, formik.values.customerEmail]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setSelectedImage(file);
  };

  const phoneValid = (val: string) => stripPhone(val).length === 10;

  return (
    <Box sx={{ bgcolor: "#f8f9fb", minHeight: "100vh" }}>
      {/* ── Top Bar ── */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", px: 3, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, maxWidth: 1440, mx: "auto" }}>
          <IconButton onClick={() => navigate("/vendor/list")} size="small" sx={{ border: "1px solid #e2e8f0", borderRadius: 2, width: 36, height: 36 }}>
            <ArrowBackIcon sx={{ fontSize: 18, color: "#64748b" }} />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={18} fontWeight={700} color="#0f172a" letterSpacing="-0.02em">Create New Supplier</Typography>
          </Box>
          <Button variant="outlined" size="small" onClick={() => { formik.resetForm(); setSelectedImage(undefined); navigate("/vendor/list"); }}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", mr: 1 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small" type="submit" disabled={!formValid || isLoading}
            onClick={() => formik.handleSubmit()}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 3,
              "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" } }}>
            {isLoading ? "Saving..." : "Create Supplier"}
          </Button>
        </Box>
      </Box>

      {/* ── Content ── */}
      <Box sx={{ mx: "auto", p: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>

            {/* ════ LEFT COLUMN ════ */}
            <Grid item xs={12} lg={8}>

              {/* Supplier Name */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Supplier Name</Typography>
                  <Chip label="Required" size="small" sx={{ height: 20, fontSize: 9, fontWeight: 700, bgcolor: "#fef2f2", color: "#dc2626", ml: 0.5 }} />
                </Box>
                <TextField fullWidth placeholder="Enter supplier or company name"
                  name="customerCompanyName"
                  value={formik.values.customerCompanyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
                  helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 15, fontWeight: 500, bgcolor: "#fff",
                    "& fieldset": { borderColor: "#e2e8f0" },
                    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 2 } } }} />
              </Paper>

              {/* Company Information */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <BusinessIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Company Information</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Company Phone</Typography>
                    <TextField fullWidth size="small" placeholder="(555) 000-0000"
                      name="customerCompanyPhoneNumebr"
                      value={formik.values.customerCompanyPhoneNumebr}
                      onChange={handlePhoneChange("customerCompanyPhoneNumebr")}
                      onBlur={formik.handleBlur}
                      error={formik.touched.customerCompanyPhoneNumebr && Boolean(formik.errors.customerCompanyPhoneNumebr)}
                      helperText={formik.touched.customerCompanyPhoneNumebr && formik.errors.customerCompanyPhoneNumebr}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                        endAdornment: formik.values.customerCompanyPhoneNumebr && phoneValid(formik.values.customerCompanyPhoneNumebr) ? (
                          <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                        ) : null,
                      }}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Company Email</Typography>
                    <TextField fullWidth size="small" placeholder="company@example.com"
                      name="customerCompanyEmail"
                      value={formik.values.customerCompanyEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.customerCompanyEmail && Boolean(formik.errors.customerCompanyEmail)}
                      helperText={formik.touched.customerCompanyEmail && formik.errors.customerCompanyEmail}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                        endAdornment: formik.values.customerCompanyEmail && !validateEmail(formik.values.customerCompanyEmail) ? (
                          <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                        ) : null,
                      }}
                      sx={fieldSx} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Contact Person */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <PersonIcon sx={{ fontSize: 18, color: "#16a34a" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Contact Person</Typography>
                </Box>
                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>First Name</Typography>
                    <TextField fullWidth size="small" placeholder="John"
                      name="customerFirstName"
                      value={formik.values.customerFirstName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Last Name</Typography>
                    <TextField fullWidth size="small" placeholder="Doe"
                      name="customerLastName"
                      value={formik.values.customerLastName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Mobile Number</Typography>
                    <TextField fullWidth size="small" placeholder="(555) 000-0000"
                      name="customerMobileNum"
                      value={formik.values.customerMobileNum}
                      onChange={handlePhoneChange("customerMobileNum")}
                      onBlur={formik.handleBlur}
                      error={formik.touched.customerMobileNum && Boolean(formik.errors.customerMobileNum)}
                      helperText={formik.touched.customerMobileNum && formik.errors.customerMobileNum}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                        endAdornment: formik.values.customerMobileNum && phoneValid(formik.values.customerMobileNum) ? (
                          <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                        ) : null,
                      }}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Email</Typography>
                    <TextField fullWidth size="small" placeholder="john@example.com"
                      name="customerEmail"
                      value={formik.values.customerEmail}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
                      helperText={formik.touched.customerEmail && formik.errors.customerEmail}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><EmailIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                        endAdornment: formik.values.customerEmail && !validateEmail(formik.values.customerEmail) ? (
                          <InputAdornment position="end"><CheckCircleIcon sx={{ fontSize: 16, color: "#16a34a" }} /></InputAdornment>
                        ) : null,
                      }}
                      sx={fieldSx} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Address */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
                  <LocationOnIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Address</Typography>
                </Box>

                {/* Address Search */}
                <Box sx={{ mb: 2.5 }}>
                  <Typography sx={labelSx}>Search Address</Typography>
                  <Box sx={{ position: "relative" }}>
                    <TextField fullWidth size="small"
                      placeholder="Start typing an address to auto-fill..."
                      value={addressInput}
                      onChange={(e) => handleAddressInputChange(e.target.value)}
                      onBlur={() => setTimeout(() => setAddressOpen(false), 200)}
                      onFocus={() => addressOptions.length > 0 && setAddressOpen(true)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} /></InputAdornment>,
                        endAdornment: addressLoading ? <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment> : null,
                      }}
                      sx={fieldSx} />
                    {addressOpen && addressOptions.length > 0 && (
                      <Paper elevation={4} sx={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1300, maxHeight: 240, overflowY: "auto", mt: 0.5, borderRadius: 2 }}>
                        {addressOptions.map((opt, idx) => (
                          <Box key={idx} onMouseDown={() => handleAddressSelect(opt)}
                            sx={{
                              display: "flex", alignItems: "flex-start", gap: 1, px: 2, py: 1.2,
                              cursor: "pointer", fontSize: 13,
                              borderBottom: idx < addressOptions.length - 1 ? "1px solid #f1f5f9" : "none",
                              "&:hover": { bgcolor: "#f8fafc" },
                            }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: "#8b5cf6", mt: 0.2, flexShrink: 0 }} />
                            <Typography fontSize={12} lineHeight={1.4} color="#334155">{opt.display_name}</Typography>
                          </Box>
                        ))}
                      </Paper>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>
                    Powered by OpenStreetMap. Select a result to auto-fill the fields below.
                  </Typography>
                </Box>

                <Divider sx={{ mb: 2.5 }}>
                  <Typography sx={{ fontSize: 10, color: "#94a3b8" }}>or fill manually</Typography>
                </Divider>

                <Grid container spacing={2.5}>
                  <Grid item xs={4} sm={2}>
                    <Typography sx={labelSx}>Street #</Typography>
                    <TextField fullWidth size="small" placeholder="#"
                      name="customerStreetNumber"
                      value={formik.values.customerStreetNumber}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={8} sm={4}>
                    <Typography sx={labelSx}>Street Name</Typography>
                    <TextField fullWidth size="small" placeholder="Main St"
                      name="customerStreetName"
                      value={formik.values.customerStreetName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>City</Typography>
                    <TextField fullWidth size="small" placeholder="City"
                      name="customerCity"
                      value={formik.values.customerCity}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Province / State</Typography>
                    <TextField fullWidth size="small" placeholder="Province"
                      name="customerProvince"
                      value={formik.values.customerProvince}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={labelSx}>Postal Code</Typography>
                    <TextField fullWidth size="small" placeholder="A1B 2C3"
                      name="customerPostalCode"
                      value={formik.values.customerPostalCode}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      sx={fieldSx} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Notes */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                  <NotesIcon sx={{ fontSize: 18, color: "#f59e0b" }} />
                  <Typography fontSize={14} fontWeight={700} color="#0f172a">Notes</Typography>
                </Box>
                <TextField fullWidth multiline rows={4} size="small" placeholder="Payment terms, delivery preferences, special instructions..."
                  name="customerDescription"
                  value={formik.values.customerDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e2e8f0" } } }} />
              </Paper>
            </Grid>

            {/* ════ RIGHT COLUMN ════ */}
            <Grid item xs={12} lg={4}>

              {/* Image */}
              <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Typography fontSize={13} fontWeight={700} color="#0f172a" mb={1.5}>Supplier Logo</Typography>
                <Box sx={{
                  width: "100%", height: 120, borderRadius: 2, bgcolor: "#f8fafc",
                  border: selectedImage ? "none" : "2px dashed #e2e8f0",
                  display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                  overflow: "hidden", position: "relative", mb: 1.5, cursor: "pointer",
                  transition: "all 0.2s", "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f1f5f9" },
                }}>
                  {selectedImage ? (
                    <>
                      <img src={URL.createObjectURL(selectedImage)} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      <IconButton size="small" onClick={() => setSelectedImage(undefined)}
                        sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(0,0,0,0.5)", color: "#fff", width: 24, height: 24, "&:hover": { bgcolor: "rgba(0,0,0,0.7)" } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </>
                  ) : (
                    <label htmlFor="supplier-img" style={{ cursor: "pointer", textAlign: "center", width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                      <CloudUploadIcon sx={{ fontSize: 28, color: "#cbd5e1", mb: 0.5 }} />
                      <Typography fontSize={11} color="#94a3b8" fontWeight={500}>Click to upload</Typography>
                      <Typography fontSize={9} color="#cbd5e1">JPG, PNG, GIF up to 2MB</Typography>
                    </label>
                  )}
                </Box>
                <input accept="image/*" style={{ display: "none" }} id="supplier-img" type="file" onChange={handleImageChange} />
                {!selectedImage && (
                  <label htmlFor="supplier-img">
                    <Button component="span" fullWidth variant="outlined" size="small" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                      sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", py: 0.75 }}>
                      Upload Image
                    </Button>
                  </label>
                )}
              </Paper>

              {/* Save Actions */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2.5, border: "1px solid #e2e8f0" }}>
                <Button fullWidth variant="contained" disableElevation disabled={!formValid || isLoading}
                  onClick={() => formik.handleSubmit()}
                  startIcon={isLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                  sx={{ borderRadius: 2, py: 1.5, textTransform: "none", fontWeight: 700, fontSize: 14,
                    bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b", boxShadow: "0 4px 14px rgba(15,23,42,0.25)" },
                    "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
                  {isLoading ? "Saving..." : "Create Supplier"}
                </Button>
                <Button fullWidth variant="text" size="small"
                  onClick={() => { formik.resetForm(); setSelectedImage(undefined); navigate("/vendor/list"); }}
                  sx={{ mt: 1, borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#94a3b8" }}>
                  Cancel
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </form>
      </Box>
    </Box>
  );
};

export default Index;
